import React, { Component } from "react";
import {
  StyleSheet,
  Modal,
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Platform,
} from "react-native";
import { connect } from "react-redux";
import { MaterialIcons, MaterialCommunityIcons } from "@expo/vector-icons";
import colors from "../constants/colors";
import { getItemsByLocation } from "../actions/records";
import { getIngredientLotsByName } from "../actions/submissions";
import { buildGetItemsByLocationPayload } from "../utils/locationScan";
import { formatLocationItemsResponse } from "./components/itemsByLocation";
import { validateDecimals } from "../utils";
import {
  getRootLocations,
  getChildLocations,
  getPartId,
  getNodeName,
  locationMatchesSearch,
  isSameLocation,
  locationKey,
  unwrapLocationValue,
  LOCATION_LEVELS,
  trimLocationToLevel,
  getDeepestLevelIndex,
} from "../utils/inventoryTransferLocationTree";
import { isMobileWeb } from "../utils/mobileWeb";
import InventoryTransferLotsPanel, {
  buildInventoryTransferSaveRows,
  findLotMissingToLocation,
  getTransferLocationLabel,
  getTransferLocationPathLabel,
  locationsRoughlyMatch,
  resolveTransferLocation,
} from "./InventoryTransferLotsPanel";
import {
  BATCH_FULLY_USED_MESSAGE,
  BATCH_PARTIAL_REMAINING_MESSAGE,
  getTransferBatchRemaining,
  getTransferBatchUsedElsewhere,
  getTransferItemRemainingAmount,
} from "../utils/inventoryTransferAllocations";

const TYPE_API = {
  finishedGoods: "fg",
  rawMaterials: "ingredient",
  packagingMaterials: "packagingMaterial",
  workInProgress: "wip",
};

const TYPE_TABS = [
  { key: "finishedGoods", label: "Finished Goods" },
  { key: "rawMaterials", label: "Raw Materials" },
  { key: "packagingMaterials", label: "Packaging Materials" },
  { key: "workInProgress", label: "WIP'S" },
];

function roundTransferAmount(value) {
  const n = parseFloat(value);
  if (isNaN(n)) return 0;
  if (n % 1 !== 0 && !validateDecimals(String(n))) {
    return Number(n.toFixed(3));
  }
  return n;
}

/**
 * Current Location modal for Inventory Transfer:
 * - Top hierarchy always shows search + Current Location selection
 * - With a preselected item, Current Location only lists locations where that item has stock
 * - Changing Current Location keeps only selected lots that exist there; others are cleared
 * - Transfer to is per lot code (one Transfer details box for the selected lot row)
 * - Available stock at Current Location, filterable by inventory type tabs
 * - Save → one submission row per lot with that lot's Transfer to
 */
class InventoryTransferFromLocationModal extends Component {
  constructor(props) {
    super(props);
    const rawFrom = unwrapLocationValue(props.fromLocation);
    const fromLocation = this.resolveCatalogLocation(rawFrom, props.inventoryLocations) || rawFrom;
    const initialType =
      props.initialItem?.inventoryCategory ||
      props.initialItem?.inventoryType ||
      "finishedGoods";
    const selectedStockType = TYPE_TABS.some((t) => t.key === initialType)
      ? initialType
      : "finishedGoods";
    this.state = {
      locationSearch: "",
      itemSearch: "",
      toLocationSearch: "",
      selectedStockType,
      fromLocation: fromLocation || null,
      options: [],
      loading: false,
      expandedRootId: null,
      branchPath: [],
      selectedLots: [],
      expandedItems: {},
      /** Lot row key whose Transfer details are shown/edited */
      selectedLotKey: null,
      /** When set, Transfer details apply to all lots of this item */
      selectedItemName: null,
      showToLocationDropdown: false,
      allowSeedInitial: true,
      hasUserChanges: false,
      pendingPruneSelectedLots: false,
      /** When set, Current Location tree is limited to these catalog paths (item has stock there). */
      itemFromLocations: null,
      /** Exact stock-location keys the user may select as Current Location. */
      itemSelectableFromKeys: null,
      infoMessage: "",
      toLocationError: "",
    };
    this._fetchId = 0;
    this._itemLocFetchId = 0;
  }

  componentDidMount() {
    const { fromLocation } = this.state;
    this.loadItemFromLocationFilter();
    if (fromLocation) {
      this.expandPathToLocation(fromLocation);
    }
    // Restore submission-saved lots immediately so completed records don't flash live DB stock.
    if (this.normalizeInitialLotcodes(this.props.initialLotcodes).length) {
      this.seedInitialSelection();
    }
    // Completed submissions only need saved data — skip live Available Stock fetch.
    if (fromLocation && !this.isSubmissionCompleted()) {
      this.fetchFromStock(fromLocation);
    }
  }

  /** Map historial/preselected location onto catalog entries so the tree can expand and re-select. */
  resolveCatalogLocation(location, inventoryLocations) {
    const all = Array.isArray(inventoryLocations) ? inventoryLocations : this.getAllLocations?.() || [];
    if (!location || !all.length) return location || null;
    const deepest = getDeepestLevelIndex(location);
    if (deepest < 0) return location;
    const match = all.find((loc) => {
      for (var i = 0; i <= deepest; i++) {
        const key = LOCATION_LEVELS[i];
        if (getPartId(loc?.[key]) !== getPartId(location?.[key])) return false;
      }
      return true;
    });
    if (match) return trimLocationToLevel(match, deepest);
    const deepKey = LOCATION_LEVELS[deepest];
    const deepId = getPartId(location[deepKey]);
    const byId = all.find((loc) => getPartId(loc?.[deepKey]) === deepId);
    return byId ? trimLocationToLevel(byId, deepest) : location;
  }

  expandPathToLocation(location) {
    const resolved = this.resolveCatalogLocation(location) || location;
    // Do not auto-expand first-level sites; keep the list collapsed until the user opens a row.
    this.setState({
      fromLocation: resolved || null,
      expandedRootId: null,
      branchPath: [],
    });
  }

  getAllLocations() {
    return Array.isArray(this.props.inventoryLocations) ? this.props.inventoryLocations : [];
  }

  getPreselectedItemName() {
    const { initialItem } = this.props;
    return (
      initialItem?.itemName ||
      initialItem?.item ||
      (typeof initialItem === "string" ? initialItem : "") ||
      ""
    );
  }

  /** Restrict Current Location picker when an item was chosen in the Item modal. */
  shouldRestrictFromLocationsByItem() {
    return !!String(this.getPreselectedItemName() || "").trim();
  }

  /**
   * Catalog used for Current Location tree.
   * With a preselected item → only locations (and ancestors) where that item has stock.
   * Transfer to still uses getAllLocations().
   */
  getFromLocationCatalog() {
    if (this.shouldRestrictFromLocationsByItem() && Array.isArray(this.state.itemFromLocations)) {
      return this.state.itemFromLocations;
    }
    return this.getAllLocations();
  }

  collectStockLocationsFromLots(lots) {
    const locs = [];
    (Array.isArray(lots) ? lots : []).forEach((lot) => {
      const historial = Array.isArray(lot?.historial) ? lot.historial : [];
      historial.forEach((h) => {
        if (h?.hide) return;
        const amt = parseFloat(h?.amount != null ? h.amount : h?.editedAmount);
        if (isNaN(amt) || amt <= 0) return;
        const loc = h?.location != null ? h.location : h?.completeLocation;
        if (loc != null && loc !== "") locs.push(loc);
      });
    });
    return locs;
  }

  locationMatchesStockPath(catalogLoc, stockLoc) {
    if (!catalogLoc || stockLoc == null || stockLoc === "") return false;
    if (stockLoc === "default_location") {
      return !!catalogLoc?.site?.defaultLocation;
    }
    const raw = unwrapLocationValue(stockLoc) || (typeof stockLoc === "object" ? stockLoc : null);
    if (!raw || typeof raw !== "object") return false;
    const deep = getDeepestLevelIndex(raw);
    if (deep < 0) {
      return !!catalogLoc?.site?.defaultLocation && !!raw?.site?.defaultLocation;
    }
    for (var i = 0; i <= deep; i++) {
      const key = LOCATION_LEVELS[i];
      if (getPartId(catalogLoc?.[key]) !== getPartId(raw?.[key])) return false;
    }
    return true;
  }

  /** Build tree catalog paths for every location where the item has stock (includes ancestors). */
  buildItemFromLocationCatalog(lots) {
    const all = this.getAllLocations();
    const stockLocs = this.collectStockLocationsFromLots(lots);
    if (!stockLocs.length) return { tree: [], selectableKeys: {} };
    const seen = {};
    const matched = [];
    const selectableKeys = {};

    const pathKey = (path, level) =>
      LOCATION_LEVELS.slice(0, level + 1)
        .map((k) => getPartId(path?.[k]))
        .filter(Boolean)
        .join(">");

    const addPath = (path, markSelectable) => {
      if (!path) return;
      const deep = getDeepestLevelIndex(path);
      if (deep < 0 && path?.site?.defaultLocation) {
        const key = "default_location";
        if (!seen[key]) {
          seen[key] = true;
          matched.push(path);
        }
        if (markSelectable) selectableKeys[key] = true;
        return;
      }
      for (var level = 0; level <= deep; level++) {
        const trimmed = trimLocationToLevel(path, level);
        const key = pathKey(trimmed, level);
        if (!key || seen[key]) {
          if (markSelectable && level === deep && key) selectableKeys[key] = true;
          continue;
        }
        seen[key] = true;
        matched.push(trimmed);
        if (markSelectable && level === deep) selectableKeys[key] = true;
      }
    };

    stockLocs.forEach((raw) => {
      if (raw === "default_location") {
        all.forEach((loc) => {
          if (loc?.site?.defaultLocation) addPath(trimLocationToLevel(loc, 0), true);
        });
        selectableKeys.default_location = true;
        return;
      }
      const unwrapped = unwrapLocationValue(raw) || (typeof raw === "object" ? raw : null);
      const resolved = this.resolveCatalogLocation(unwrapped || raw, all);
      if (resolved) addPath(resolved, true);
      const deep = getDeepestLevelIndex(unwrapped || raw);
      all.forEach((catalog) => {
        if (!this.locationMatchesStockPath(catalog, raw)) return;
        addPath(deep >= 0 ? trimLocationToLevel(catalog, deep) : catalog, true);
      });
    });

    return { tree: matched, selectableKeys };
  }

  applyItemFromLocationFilter(lots) {
    const { tree, selectableKeys } = this.buildItemFromLocationCatalog(lots);
    this.setState({
      itemFromLocations: tree,
      itemSelectableFromKeys: selectableKeys,
    });
  }

  loadItemFromLocationFilter() {
    if (!this.shouldRestrictFromLocationsByItem()) {
      this.setState({ itemFromLocations: null, itemSelectableFromKeys: null });
      return;
    }
    const { initialItem, initialLotcodes } = this.props;
    const syncLots = [
      ...(Array.isArray(initialItem?.items) ? initialItem.items : []),
      ...this.normalizeInitialLotcodes(initialLotcodes),
    ];
    if (syncLots.length) {
      this.applyItemFromLocationFilter(syncLots);
    }

    const name = this.getPreselectedItemName();
    const type =
      initialItem?.type ||
      TYPE_API[initialItem?.inventoryCategory] ||
      TYPE_API[initialItem?.inventoryType] ||
      "ingredient";
    if (!name || !this.props.getIngredientLotsByName) return;

    const fetchId = ++this._itemLocFetchId;
    this.props.getIngredientLotsByName({ name, type }, (res) => {
      if (fetchId !== this._itemLocFetchId) return;
      const lots = res?.lots || [];
      if (!lots.length) return;
      this.applyItemFromLocationFilter(lots);
    });
  }

  isAllowedFromLocationSelection(location) {
    if (!this.shouldRestrictFromLocationsByItem()) return true;
    const keys = this.state.itemSelectableFromKeys;
    if (!keys || !Object.keys(keys).length) return true;
    if (location === "default_location" || (location?.site?.defaultLocation && getDeepestLevelIndex(location) <= 0)) {
      return !!keys.default_location;
    }
    const deep = getDeepestLevelIndex(location);
    if (deep < 0) return false;
    const key = LOCATION_LEVELS.slice(0, deep + 1)
      .map((k) => getPartId(location?.[k]))
      .filter(Boolean)
      .join(">");
    return !!keys[key];
  }

  getLocationLabel(location) {
    return getTransferLocationLabel(location);
  }

  getLocationPathLabel(location) {
    return getTransferLocationPathLabel(location);
  }

  getFromLocationLabel() {
    return this.getLocationPathLabel(this.state.fromLocation);
  }

  isReadOnly() {
    return !!this.props.readOnly;
  }

  isSubmissionCompleted() {
    return this.isReadOnly();
  }

  getVisibleRootLocations() {
    const { locationSearch } = this.state;
    const roots = getRootLocations(this.getFromLocationCatalog());
    const q = (locationSearch || "").trim().toLowerCase();
    if (!q) return roots;
    const all = this.getFromLocationCatalog();
    return roots.filter((root) => {
      if (locationMatchesSearch(root, q)) return true;
      const rootId = getPartId(root.site);
      return all.some((loc) => getPartId(loc?.site) === rootId && locationMatchesSearch(loc, q));
    });
  }

  getToLocationOptions() {
    const { toLocationSearch } = this.state;
    const all = this.getAllLocations();
    const q = (toLocationSearch || "").trim().toLowerCase();
    if (!q) return all;
    return all.filter((loc) => locationMatchesSearch(loc, q));
  }

  hasChildren(location) {
    return getChildLocations(this.getFromLocationCatalog(), location).length > 0;
  }

  setFromLocation(location) {
    if (this.isReadOnly()) return;
    if (!this.isAllowedFromLocationSelection(location)) return;
    const resolved = this.resolveCatalogLocation(location) || location;
    const prev = this.state.fromLocation;
    const changed = !isSameLocation(prev, resolved);
    this.setState(
      {
        fromLocation: resolved,
        ...(changed
          ? {
              showToLocationDropdown: false,
              selectedLotKey: null,
              selectedItemName: null,
              allowSeedInitial: false,
              options: [],
              hasUserChanges: true,
              infoMessage: "",
              /** After stock loads, drop selected lots that are not at the new location. */
              pendingPruneSelectedLots: true,
            }
          : {}),
      },
      () => {
        if (changed || !this.state.options.length) this.fetchFromStock(resolved);
      }
    );
  }

  /** Name tap: select only. Always collapses children of this first-level site. */
  selectFromRootLocation(location) {
    this.setState(
      { expandedRootId: null, branchPath: [] },
      () => {
        this.setFromLocation(location);
        this.toggleRootExpand(location);
      }
    );
  }

  /** Arrow tap: expand/collapse only — does not select. */
  toggleRootExpand(location) {
    const rootId = getPartId(location?.site);
    const { expandedRootId } = this.state;
    const collapsing = expandedRootId === rootId;
    this.setState({
      expandedRootId: collapsing ? null : rootId,
      branchPath: collapsing ? [] : this.state.branchPath,
    });
  }

  /**
   * Name tap: select only. Does not expand children — clears path at this column
   * so any open child levels collapse.
   */
  selectFromBranchLocation(location, columnIndex) {
    const { branchPath } = this.state;
    const nextPath = branchPath.slice(0, columnIndex);
    this.setState({ branchPath: nextPath }, () => {this.setFromLocation(location); this.toggleBranchExpand(location, columnIndex)});
  }

  /** Arrow tap: expand/collapse children only — does not select. */
  toggleBranchExpand(location, columnIndex) {
    const { branchPath } = this.state;
    const alreadyExpanded =
      branchPath[columnIndex] && isSameLocation(branchPath[columnIndex], location);
    if (alreadyExpanded) {
      this.setState({ branchPath: branchPath.slice(0, columnIndex) });
      return;
    }
    const nextPath = branchPath.slice(0, columnIndex);
    nextPath[columnIndex] = location;
    this.setState({ branchPath: nextPath });
  }

  selectToLocation(location, lotKey, itemName) {
    if (this.isReadOnly()) return false;
    const bulkItemName = itemName || (!lotKey ? this.state.selectedItemName : null);
    const key = lotKey || this.state.selectedLotKey;
    const lotsToCheck = bulkItemName
      ? this.state.selectedLots.filter((l) => (l.item || l.itemName) === bulkItemName)
      : this.state.selectedLots.filter((l) => l.key === key);
    const sharedFrom = this.state.fromLocation || null;
    const sameAsCurrent = lotsToCheck.some((l) =>
      locationsRoughlyMatch(l.location || sharedFrom, location)
    );
    if (sameAsCurrent) {
      this.setState({
        showToLocationDropdown: true,
        toLocationError: "Destination location must be different from the current location.",
      });
      return false;
    }
    let selectedLots;
    if (bulkItemName) {
      selectedLots = this.state.selectedLots.map((l) =>
        (l.item || l.itemName) === bulkItemName ? { ...l, toLocation: location } : l
      );
    } else {
      if (!key) return false;
      selectedLots = this.state.selectedLots.map((l) =>
        l.key === key ? { ...l, toLocation: location } : l
      );
    }
    this.setState({
      selectedLots,
      showToLocationDropdown: false,
      toLocationSearch: "",
      hasUserChanges: true,
      infoMessage: "",
      toLocationError: "",
    });
    return true;
  }

  selectLotRow(lotKey) {
    this.setState({
      selectedLotKey: lotKey,
      selectedItemName: null,
      showToLocationDropdown: false,
      toLocationSearch: "",
    });
  }

  selectItem(itemName) {
    if (!itemName) return;
    this.setState({
      selectedItemName: itemName,
      selectedLotKey: null,
      showToLocationDropdown: false,
      toLocationSearch: "",
    });
  }

  getBranchColumns(rootLocation) {
    const { branchPath } = this.state;
    const columns = [];
    const all = this.getFromLocationCatalog();
    let parent = rootLocation;
    let col = 0;
    while (parent) {
      const children = getChildLocations(all, parent);
      if (!children.length) break;
      columns.push({ parent, children, selected: branchPath[col] || null });
      parent = branchPath[col] || null;
      col += 1;
      if (col > LOCATION_LEVELS.length) break;
    }
    return columns;
  }

  renderLocationTree() {
    const { expandedRootId, fromLocation, branchPath } = this.state;
    const roots = this.getVisibleRootLocations();

    return (
      <ScrollView style={styles.locationsScroll} nestedScrollEnabled>
        {roots.map((root, idx) => {
          const rootId = getPartId(root.site);
          const expanded = expandedRootId === rootId;
          const selected = isSameLocation(fromLocation, root);
          const children = getChildLocations(this.getFromLocationCatalog(), root);
          const hasKids = children.length > 0;
          const columns = expanded ? this.getBranchColumns(root) : [];

          return (
            <View key={rootId || idx}>
              <View
                style={[
                  styles.rootRow,
                  idx % 2 === 0 ? styles.rootRowAlt : styles.rootRowBase,
                  selected && styles.rootRowSelected,
                ]}
              >
                <TouchableOpacity
                  style={styles.rootRowLabelBtn}
                  onPress={() => this.selectFromRootLocation(root)}
                >
                  <Text
                    style={[styles.rootRowTxt, selected && styles.rootRowTxtSelected]}
                    numberOfLines={2}
                  >
                    {getNodeName(root) || this.getLocationLabel(root)}
                  </Text>
                </TouchableOpacity>
                {hasKids ? (
                  <TouchableOpacity
                    style={styles.expandBtn}
                    onPress={() => this.toggleRootExpand(root)}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  >
                    <MaterialIcons
                      name={expanded ? "keyboard-arrow-down" : "keyboard-arrow-right"}
                      size={24}
                      color={expanded ? colors.primary : "#555"}
                    />
                  </TouchableOpacity>
                ) : null}
              </View>

              {expanded && columns.length > 0 ? (
                <ScrollView horizontal nestedScrollEnabled style={styles.branchScroll}>
                  <View style={styles.branchRowWrap}>
                    {columns.map((column, colIdx) => (
                      <View key={`col-${colIdx}`} style={styles.branchCol}>
                        {column.children.map((child, childIdx) => {
                          const inPath = isSameLocation(branchPath[colIdx], child);
                          const childSelected = isSameLocation(fromLocation, child);
                          const childHasKids = this.hasChildren(child);
                          const highlight = inPath || childSelected;
                          return (
                            <View
                              key={locationKey(child) || `${colIdx}-${childIdx}`}
                              style={[
                                styles.branchRow,
                                highlight && styles.branchRowSelected,
                              ]}
                            >
                              <TouchableOpacity
                                style={styles.branchLabelBtn}
                                onPress={() => this.selectFromBranchLocation(child, colIdx)}
                              >
                                <Text
                                  style={[
                                    styles.branchTxt,
                                    highlight && styles.branchTxtSelected,
                                  ]}
                                  numberOfLines={2}
                                >
                                  {getNodeName(child)}
                                </Text>
                              </TouchableOpacity>
                              {childHasKids ? (
                                <TouchableOpacity
                                  style={styles.expandBtn}
                                  onPress={() => this.toggleBranchExpand(child, colIdx)}
                                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                >
                                  <MaterialIcons
                                    name={inPath ? "keyboard-arrow-down" : "keyboard-arrow-right"}
                                    size={20}
                                    color={inPath ? colors.primary : "#555"}
                                  />
                                </TouchableOpacity>
                              ) : null}
                            </View>
                          );
                        })}
                      </View>
                    ))}
                  </View>
                </ScrollView>
              ) : null}
            </View>
          );
        })}
      </ScrollView>
    );
  }

  fetchFromStock(location) {
    if (!location) {
      this.setState({ options: [], loading: false }, () => this.afterStockLoaded([]));
      return;
    }
    const payload = buildGetItemsByLocationPayload(location, this.props.inventoryLocations);
    if (!payload?.data) {
      this.setState({ options: [], loading: false }, () => this.afterStockLoaded([]));
      return;
    }
    const fetchId = ++this._fetchId;
    this.setState({ loading: true, options: [] });
    this.props.getItemsByLocation(payload.data, (res) => {
      if (fetchId !== this._fetchId) return;
      const options = formatLocationItemsResponse(res, "itemsByLocation");
      this.setState({ options, loading: false }, () => this.afterStockLoaded(options));
    });
  }

  afterStockLoaded(options) {
    if (this.state.pendingPruneSelectedLots) {
      this.pruneSelectedLotsAgainstOptions(options);
      return;
    }
    this.seedInitialSelection();
  }

  /**
   * Keep selected lots that still exist at the new Current Location;
   * clear the rest so the user can pick stock from Available Stock.
   */
  pruneSelectedLotsAgainstOptions(options) {
    const list = Array.isArray(options) ? options : [];
    const fromLocation = this.state.fromLocation;
    const used = {};
    const next = [];

    (this.state.selectedLots || []).forEach((lot) => {
      const itemName = lot.item || lot.itemName || "";
      const historialId = lot.historialId || lot.historial?.[0]?._id || null;
      let match = null;
      if (historialId) {
        match = list.find(
          (o) => String(o.historialId || o.historial?.[0]?._id || "") === String(historialId)
        );
      }
      if (!match && lot.batchId) {
        match = list.find((o) => o.batchId === lot.batchId);
      }
      if (!match && itemName) {
        match = list.find((o) => {
          const optKey = o.historialId || o.batchId || `${o.item}-${o.lot}`;
          if (used[optKey]) return false;
          return (o.item || "") === itemName && String(o.lot || "") === String(lot.lot || "");
        });
      }
      if (!match) return;

      const optKey =
        match.historialId ||
        match.historial?.[0]?._id ||
        match.batchId ||
        `${match.item}-${match.lot}`;
      used[optKey] = true;
      const batch = match?.historial?.[0] || {};
      const loc = batch?.location || batch?.completeLocation || fromLocation;
      const stockAmt = parseFloat(
        batch?.editedAmount != null ? batch.editedAmount : match?.totalAmount
      );
      let editedAmount = lot.editedAmount;
      if (editedAmount != null && editedAmount !== "" && !isNaN(stockAmt)) {
        const edited = parseFloat(editedAmount);
        if (!isNaN(edited) && edited > stockAmt) editedAmount = roundTransferAmount(stockAmt);
        else if (!isNaN(edited)) editedAmount = roundTransferAmount(edited);
      } else if ((editedAmount == null || editedAmount === "") && !isNaN(stockAmt)) {
        editedAmount = roundTransferAmount(stockAmt);
      }

      const matchedHistorialId = match.historialId || batch._id || historialId || null;
      next.push({
        ...lot,
        key: matchedHistorialId || match.batchId || lot.key,
        item: match.item || itemName,
        itemName: match.item || itemName,
        lot: match.lot != null ? match.lot : lot.lot,
        editedAmount,
        units: match.units || lot.units,
        location: loc,
        locationLabel: this.getLocationLabel(loc) || this.getFromLocationLabel(),
        type: TYPE_API[match?.inventoryCategory] || lot.type,
        inventoryType: match?.inventoryCategory || match?.inventoryType || lot.inventoryType,
        _id: match?._id || lot._id,
        productCode: match?.productCode || lot.productCode,
        historial: match?.historial || lot.historial || [],
        historialId: matchedHistorialId,
        batchId: match?.batchId || lot.batchId || matchedHistorialId || null,
        items: match?.items,
      });
    });

    const remainingNames = new Set(next.map((l) => l.item || l.itemName).filter(Boolean));
    const expandedItems = {};
    remainingNames.forEach((name) => {
      expandedItems[name] = false;
    });
    const firstItemName = next[0] ? next[0].item || next[0].itemName || null : null;

    this.setState({
      selectedLots: next,
      expandedItems,
      selectedLotKey: null,
      selectedItemName: firstItemName,
      pendingPruneSelectedLots: false,
      showToLocationDropdown: false,
    });
  }

  normalizeInitialLotcodes(raw) {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : [raw];
    return list
      .filter((l) => {
        if (l == null || l === "") return false;
        if (typeof l === "string") return String(l).trim() !== "";
        if (typeof l === "object") {
          return !!(l.lot || l.batchId || (Array.isArray(l.historial) && l.historial.length));
        }
        return false;
      })
      .map((l) => (typeof l === "string" ? { lot: l, historial: [] } : l));
  }

  getSavedEditedAmount(saved) {
    if (!saved) return null;
    if (saved.editedAmount != null && saved.editedAmount !== "") {
      const direct = parseFloat(saved.editedAmount);
      if (!isNaN(direct)) return direct;
    }
    const h = saved.historial?.[0];
    if (h) {
      const amt = parseFloat(h.editedAmount != null && h.editedAmount !== "" ? h.editedAmount : h.amount);
      if (!isNaN(amt)) return amt;
    }
    return null;
  }

  /** Restore submission-saved lots when present; otherwise auto-select stock for a new item. */
  seedInitialSelection() {
    const { initialItem, initialLotcodes } = this.props;
    const { selectedLots, options, allowSeedInitial, fromLocation } = this.state;
    if (!allowSeedInitial || selectedLots.length) return;

    const savedLots = this.normalizeInitialLotcodes(initialLotcodes);
    const name =
      initialItem?.itemName ||
      initialItem?.item ||
      (typeof initialItem === "string" ? initialItem : "") ||
      savedLots[0]?.item ||
      "";

    const itemRows = name
      ? (options || []).filter((row) => (row?.item || "") === name)
      : [];

    // Always prefer saved submission lotcodes (completed records + prior modal saves).
    // Do not replace with live DB stock when saved lots exist.
    if (savedLots.length) {
      const next = [];
      const usedOptionKeys = {};
      savedLots.forEach((saved, idx) => {
        const itemName = saved.item || name || "";
        let match = null;
        if (saved.batchId) {
          match = itemRows.find((o) => o.batchId === saved.batchId);
        }
        if (!match && saved.lot != null && String(saved.lot) !== "") {
          match = itemRows.find((o) => {
            const optKey = o.batchId || `${o.item}-${o.lot}`;
            if (usedOptionKeys[optKey]) return false;
            return (o.item || "") === itemName && String(o.lot || "") === String(saved.lot);
          });
        }

        const savedAmt = this.getSavedEditedAmount(saved);
        const savedHistorial = Array.isArray(saved.historial) ? saved.historial : [];
        const h = savedHistorial[0] || match?.historial?.[0] || {};
        const loc =
          h.location ||
          h.completeLocation ||
          fromLocation ||
          match?.historial?.[0]?.location;
        const amount =
          savedAmt != null
            ? savedAmt
            : parseFloat(h.editedAmount != null ? h.editedAmount : h.amount);

        if (match) {
          const optKey = match.batchId || `${match.item}-${match.lot}`;
          usedOptionKeys[optKey] = true;
        }

        next.push({
          key: saved.batchId || match?.batchId || `saved-${itemName}-${saved.lot}-${idx}`,
          item: itemName || match?.item || "",
          itemName: itemName || match?.item || "",
          lot: saved.lot != null ? saved.lot : match?.lot || "",
          editedAmount: !isNaN(amount) ? roundTransferAmount(amount) : 0,
          stockAmount: (() => {
            const live = parseFloat(
              match?.historial?.[0]?.editedAmount != null
                ? match.historial[0].editedAmount
                : match?.historial?.[0]?.amount != null
                  ? match.historial[0].amount
                  : match?.totalAmount
            );
            if (!isNaN(live) && live > 0) return roundTransferAmount(live);
            return !isNaN(amount) ? roundTransferAmount(amount) : 0;
          })(),
          units: saved.units || h.units || match?.units || initialItem?.units || "",
          location: loc,
          locationLabel:
            this.getLocationLabel(loc) ||
            h.completeLocation ||
            this.getFromLocationLabel(),
          type: initialItem?.type || TYPE_API[match?.inventoryCategory] || match?.type || "",
          inventoryType:
            initialItem?.inventoryType ||
            initialItem?.inventoryCategory ||
            match?.inventoryCategory ||
            "",
          _id: initialItem?._id || match?._id,
          productCode: initialItem?.productCode || match?.productCode || h.productCode || "",
          historial: savedHistorial.length ? savedHistorial : match?.historial || [],
          historialId: h._id || saved.historialId || match?.historialId || null,
          batchId: saved.batchId || match?.batchId || null,
          items: match?.items,
          toLocation:
            unwrapLocationValue(saved.toLocation) ||
            unwrapLocationValue(this.props.toLocation) ||
            null,
        });
      });

      const expandedItems = {};
      let firstItemName = null;
      next.forEach((lot) => {
        const itemName = lot.item || lot.itemName;
        if (itemName) {
          expandedItems[itemName] = false;
          if (!firstItemName) firstItemName = itemName;
        }
      });
      this.setState({
        selectedLots: next,
        expandedItems,
        selectedLotKey: null,
        selectedItemName: firstItemName,
        allowSeedInitial: false,
      });
      return;
    }

    // First open after item + FIFO location (no saved lots yet): select every lot at location.
    if (name && itemRows.length) {
      this.addLotsFromRows(itemRows, true);
      this.setState({ allowSeedInitial: false });
      return;
    }

    this.setState({ allowSeedInitial: false });
  }

  getVisibleStockRows() {
    const { options, itemSearch, selectedStockType } = this.state;
    const q = (itemSearch || "").trim().toLowerCase();
    return (options || []).filter((row) => {
      const category = row?.inventoryCategory || row?.inventoryType || "";
      if (selectedStockType && category !== selectedStockType) return false;
      if (!q) return true;
      const name = String(row?.item || "").toLowerCase();
      const code = String(row?.productCode || "").toLowerCase();
      const lot = String(row?.lot || "").toLowerCase();
      const typeLabel = String(row?.inventoryTypeLabel || category || "").toLowerCase();
      return name.includes(q) || code.includes(q) || lot.includes(q) || typeLabel.includes(q);
    });
  }

  onChangeStockType(typeKey) {
    this.setState({ selectedStockType: typeKey, itemSearch: "" });
  }

  getAggregatedStock() {
    // Display: one row per item with remaining transferable amount at Current Location
    // (on-hand minus qty already used on other transfer lines / in this modal).
    // Selection still uses `rows` (every lot/batch at this location).
    const batchRows = this.expandStockRowsToBatches(this.getVisibleStockRows());
    const byKey = {};
    const fromLocation = this.state.fromLocation;
    batchRows.forEach((row) => {
      const name = row?.item || "";
      const id = row?._id != null ? String(row._id) : name;
      const key = `${row?.inventoryCategory || ""}::${id}::${name}`;
      if (!byKey[key]) {
        byKey[key] = {
          item: name,
          itemName: name,
          _id: row?._id,
          type: TYPE_API[row?.inventoryCategory] || "ingredient",
          inventoryType: row?.inventoryCategory || row?.inventoryType || "",
          productCode: row?.productCode || "",
          units: row?.units || "",
          totalAmount: 0,
          location: fromLocation,
          locationLabel: this.getFromLocationLabel(),
          rows: [],
        };
      }
      const amt = parseFloat(row?.totalAmount);
      if (!isNaN(amt)) byKey[key].totalAmount += amt;
      byKey[key].rows.push(row);
    });
    const allocCtx = {
      submission: this.props.submission,
      components: this.props.components,
      position: this.props.position,
      selectedLots: this.state.selectedLots,
    };
    return Object.values(byKey).map((agg) => ({
      ...agg,
      totalAmount: roundTransferAmount(
        getTransferItemRemainingAmount(agg.totalAmount, agg.item || agg.itemName, allocCtx)
      ),
    }));
  }

  getHistorialId(row) {
    if (row?.historialId) return row.historialId;
    const h = row?.historial?.[0];
    return h?._id || null;
  }

  lotAlreadySelected(row, list) {
    const selectedLots = list || this.state.selectedLots;
    const historialId = this.getHistorialId(row);
    if (historialId) {
      return selectedLots.some(
        (l) => String(l.historialId || l.historial?.[0]?._id || "") === String(historialId)
      );
    }
    const batchId = row?.batchId;
    if (batchId) {
      return selectedLots.some((l) => {
        if (l.historialId || l.historial?.[0]?._id) return false;
        return l.batchId === batchId;
      });
    }
    const fromLocation = this.state.fromLocation;
    return selectedLots.some(
      (l) =>
        l.item === row.item &&
        String(l.lot || "") === String(row.lot || "") &&
        locationKey(l.location) ===
          locationKey(row?.historial?.[0]?.location || fromLocation)
    );
  }

  getBatchRemainingForAdd(batchRef, stockAmount, selectedLotsList) {
    const used = getTransferBatchUsedElsewhere({
      submission: this.props.submission,
      components: this.props.components,
      position: this.props.position,
      selectedLots: selectedLotsList,
      batchRef,
      excludeSelectedKey: null,
    });
    return getTransferBatchRemaining(stockAmount, used);
  }

  getBatchRemainingForEdit(lot, selectedLotsList) {
    if (!lot) return 0;
    const stockAmount =
      lot.stockAmount != null
        ? lot.stockAmount
        : parseFloat(
            lot.historial?.[0]?.amount != null
              ? lot.historial[0].amount
              : lot.editedAmount
          );
    const used = getTransferBatchUsedElsewhere({
      submission: this.props.submission,
      components: this.props.components,
      position: this.props.position,
      selectedLots: selectedLotsList || this.state.selectedLots,
      batchRef: lot,
      excludeSelectedKey: lot.key,
    });
    return getTransferBatchRemaining(stockAmount, used);
  }

  /**
   * Expand stock rows into one entry per historial batch with stock at Current Location.
   */
  expandStockRowsToBatches(rows) {
    const fromLocation = this.state.fromLocation;
    const catalog = this.getAllLocations();
    const out = [];

    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const historial = Array.isArray(row?.historial) ? row.historial : [];
      if (!historial.length) {
        const amt = parseFloat(row?.totalAmount ?? row?.amount);
        if (!isNaN(amt) && amt > 0) out.push(row);
        return;
      }

      historial.forEach((batch, idx) => {
        if (batch?.hide) return;
        const amt = parseFloat(batch?.editedAmount != null ? batch.editedAmount : batch?.amount);
        if (isNaN(amt) || amt <= 0) return;

        if (fromLocation && historial.length > 1) {
          const rawLoc = batch.location || batch.completeLocation;
          if (rawLoc) {
            const resolved = resolveTransferLocation(rawLoc, catalog) || rawLoc;
            const fromLabel = this.getFromLocationLabel();
            const atLocation =
              locationsRoughlyMatch(rawLoc, fromLocation) ||
              locationsRoughlyMatch(resolved, fromLocation) ||
              (fromLabel &&
                (String(batch.completeLocation || "") === fromLabel ||
                  getTransferLocationPathLabel(resolved) === fromLabel ||
                  getTransferLocationLabel(resolved) === fromLabel));
            if (!atLocation) return;
          }
        }

        const historialId = batch?._id || null;
        out.push({
          ...row,
          lot: batch.lot != null ? batch.lot : row.lot,
          historial: [batch],
          historialId,
          batchId: historialId
            ? `${row.inventoryCategory || row.inventoryType || "item"}-${historialId}`
            : `${row.batchId || row.item || "row"}-${idx}`,
          totalAmount: amt,
          units: batch.units || row.units,
        });
      });
    });

    return out;
  }

  addLotsFromRows(rows, replaceSeed, { fromUser } = {}) {
    const expanded = this.expandStockRowsToBatches(rows);
    if (!expanded.length) return { added: 0, skippedFullyUsed: false };
    const { selectedLots, fromLocation } = this.state;
    let next = replaceSeed ? selectedLots.filter((l) => !l.isSeed) : [...selectedLots];
    const itemName = expanded[0]?.item || "";
    const defaultTo = unwrapLocationValue(this.props.toLocation) || null;
    const addedKeys = [];
    let skippedFullyUsed = false;
    expanded.forEach((row) => {
      if (this.lotAlreadySelected(row, next) && !replaceSeed) return;
      if (replaceSeed && this.lotAlreadySelected(row, next)) return;
      const batch = row?.historial?.[0] || {};
      const historialId = row.historialId || batch._id || null;
      const loc = batch?.location || batch?.completeLocation || fromLocation;
      const stockAmount = parseFloat(
        batch?.editedAmount != null ? batch.editedAmount : row?.totalAmount
      );
      const batchRef = {
        historialId,
        batchId: row?.batchId || historialId || null,
        historial: row?.historial?.length ? row.historial : batch && Object.keys(batch).length ? [batch] : [],
      };
      const remaining = this.getBatchRemainingForAdd(
        batchRef,
        !isNaN(stockAmount) ? stockAmount : 0,
        next
      );
      if (!(remaining > 0)) {
        skippedFullyUsed = true;
        return;
      }
      const key =
        historialId ||
        row.batchId ||
        `${row.item}-${row.lot}-${next.length}`;
      addedKeys.push(key);
      next.push({
        key,
        item: row.item,
        itemName: row.item,
        lot: row.lot || "",
        editedAmount: roundTransferAmount(remaining),
        stockAmount: !isNaN(stockAmount) ? roundTransferAmount(stockAmount) : remaining,
        units: row.units || "",
        location: loc,
        locationLabel: this.getLocationLabel(loc) || this.getFromLocationLabel(),
        type: TYPE_API[row?.inventoryCategory] || "ingredient",
        inventoryType: row?.inventoryCategory || row?.inventoryType || "",
        _id: row?._id,
        productCode: row?.productCode || "",
        historial: row?.historial?.length ? row.historial : batch && Object.keys(batch).length ? [batch] : [],
        historialId,
        batchId: row?.batchId || historialId || null,
        items: row?.items,
        toLocation: defaultTo,
      });
    });
    if (!addedKeys.length && !replaceSeed) {
      if (fromUser && skippedFullyUsed) {
        this.setState({ infoMessage: BATCH_FULLY_USED_MESSAGE });
      }
      return { added: 0, skippedFullyUsed };
    }
    const itemNameForSelect = itemName || next[0]?.item || next[0]?.itemName || null;
    const expandedItems = {
      ...this.state.expandedItems,
      ...(itemNameForSelect ? { [itemNameForSelect]: false } : {}),
    };
    this.setState({
      selectedLots: next,
      expandedItems,
      selectedLotKey: null,
      selectedItemName: itemNameForSelect,
      allowSeedInitial: false,
      ...(fromUser
        ? {
            hasUserChanges: true,
            infoMessage: "",
          }
        : {}),
    });
    return { added: addedKeys.length, skippedFullyUsed };
  }

  onAddStockItem(row) {
    if (this.isReadOnly()) return;
    // Add every lot/batch for this item at the current location (not FIFO-only).
    const rows = row?.rows?.length ? row.rows : [row];
    this.addLotsFromRows(rows, false, { fromUser: true });
  }

  removeLot(key) {
    if (this.isReadOnly()) return;
    const removed = this.state.selectedLots.find((l) => l.key === key);
    const selectedLots = this.state.selectedLots.filter((l) => l.key !== key);
    const updates = { selectedLots, hasUserChanges: true };
    if (this.state.selectedLotKey === key) {
      updates.selectedLotKey = null;
      updates.showToLocationDropdown = false;
    }
    const removedItem = removed?.item || removed?.itemName;
    if (removedItem && this.state.selectedItemName === removedItem) {
      const stillHasItem = selectedLots.some((l) => (l.item || l.itemName) === removedItem);
      if (!stillHasItem) {
        updates.selectedItemName = selectedLots[0]
          ? selectedLots[0].item || selectedLots[0].itemName || null
          : null;
      }
    }
    if (!selectedLots.length) {
      updates.selectedItemName = null;
      updates.selectedLotKey = null;
    }
    this.setState(updates);
  }

  editLotAmount(key, value) {
    if (this.isReadOnly()) return;
    if (value !== "" && !validateDecimals(value)) return;
    const lot = this.state.selectedLots.find((l) => l.key === key);
    if (!lot) return;

    if (value !== "" && value != null) {
      const requested = parseFloat(value);
      if (!isNaN(requested) && requested > 0) {
        const remaining = this.getBatchRemainingForEdit(lot);
        if (!(remaining > 0)) {
          this.setState({ infoMessage: BATCH_FULLY_USED_MESSAGE });
          return;
        }
        if (requested > remaining) {
          const selectedLots = this.state.selectedLots.map((l) =>
            l.key === key ? { ...l, editedAmount: roundTransferAmount(remaining) } : l
          );
          this.setState({
            selectedLots,
            hasUserChanges: true,
            infoMessage: BATCH_PARTIAL_REMAINING_MESSAGE,
          });
          return;
        }
      }
    }

    const selectedLots = this.state.selectedLots.map((l) =>
      l.key === key ? { ...l, editedAmount: value } : l
    );
    this.setState({ selectedLots, hasUserChanges: true, infoMessage: "" });
  }

  toggleItemExpanded(itemName) {
    const expandedItems = { ...this.state.expandedItems };
    const nextExpanded = expandedItems[itemName] !== true;
    expandedItems[itemName] = nextExpanded;
    const updates = { expandedItems };
    if (!nextExpanded) {
      updates.selectedItemName = itemName;
      updates.selectedLotKey = null;
      updates.showToLocationDropdown = false;
      updates.toLocationSearch = "";
    }
    this.setState(updates);
  }

  formatAmount(amount, units) {
    const amt = parseFloat(amount);
    if (isNaN(amt)) return units || "";
    const shown =
      amt % 1 !== 0 && !validateDecimals(String(amt))
        ? Number(amt.toFixed(3))
        : amt;
    return units ? `${shown} ${units}` : String(shown);
  }

  buildSaveRows() {
    return buildInventoryTransferSaveRows(
      this.state.selectedLots,
      this.state.fromLocation,
      this.getAllLocations()
    );
  }

  onSave() {
    const { onSave, onClose } = this.props;
    const { fromLocation, selectedLots } = this.state;
    if (!fromLocation) {
      this.setState({ infoMessage: "Please select a Current Location." });
      return;
    }
    const activeLots = selectedLots.filter((l) => !l.isSeed || l.lot);
    if (!activeLots.length) {
      this.setState({ infoMessage: "Please select at least one item to transfer." });
      return;
    }
    const missingTo = findLotMissingToLocation(activeLots, this.props.toLocation);
    if (missingTo) {
      const label = missingTo.lot || "No Lot";
      const item = missingTo.item || missingTo.itemName || "item";
      this.selectLotRow(missingTo.key);
      this.setState({
        infoMessage: `Please select a Transfer to location for lot "${label}" (${item}).`,
      });
      return;
    }
    const rows = this.buildSaveRows();
    if (!rows.length) {
      this.setState({ infoMessage: "Please select at least one item to transfer." });
      return;
    }
    this.setState({ infoMessage: "" });
    if (onSave) onSave(rows);
    if (onClose) onClose();
  }

  renderSelectedSection() {
    if (!this.state.selectedLots?.length) return null;
    return (
      <InventoryTransferLotsPanel
        selectedLots={this.state.selectedLots}
        selectedLotKey={this.state.selectedLotKey}
        selectedItemName={this.state.selectedItemName}
        expandedItems={this.state.expandedItems}
        showToLocationDropdown={this.state.showToLocationDropdown}
        toLocationSearch={this.state.toLocationSearch}
        inventoryLocations={this.getAllLocations()}
        readOnly={this.isReadOnly()}
        fallbackToLocation={this.props.toLocation}
        sharedFromLocationLabel={this.getFromLocationLabel()}
        toLocationError={this.state.toLocationError}
        onDismissToLocationError={() =>
          this.setState({ toLocationError: "", showToLocationDropdown: true })
        }
        onSelectLotKey={(key) => this.selectLotRow(key)}
        onSelectItem={(name) => this.selectItem(name)}
        onRemoveLot={(key) => this.removeLot(key)}
        onEditLotAmount={(key, val) => this.editLotAmount(key, val)}
        onToggleItemExpanded={(name) => this.toggleItemExpanded(name)}
        onToggleToLocationDropdown={(open) =>
          this.setState({
            showToLocationDropdown: !!open,
            toLocationSearch: open ? this.state.toLocationSearch : "",
          })
        }
        onChangeToLocationSearch={(val) => this.setState({ toLocationSearch: val })}
        onSelectToLocation={(loc, lotKey, itemName) =>
          this.selectToLocation(loc, lotKey, itemName)
        }
      />
    );
  }

  render() {
    const { onClose } = this.props;
    const { locationSearch, itemSearch, loading, fromLocation, hasUserChanges, selectedStockType } = this.state;
    const stock = this.getAggregatedStock();
    const fromLabel = this.getFromLocationLabel();
    const fromChipLabel = fromLabel;
    const readOnly = this.isReadOnly();
    const hideAvailableStock = this.isSubmissionCompleted();

    return (
      <Modal animationType="slide" transparent={false} visible={true} onRequestClose={onClose}>
        <View style={styles.screen}>
          <View style={styles.card}>
            <View style={styles.header}>
              <TouchableOpacity style={styles.backBtn} onPress={onClose}>
                <MaterialIcons name="arrow-back" size={26} color={colors.primary} />
              </TouchableOpacity>
              <Text style={styles.headerTitle}>Locations</Text>
              <View style={styles.backBtn} />
            </View>

            <ScrollView style={styles.bodyScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
              <Text style={styles.sectionLabel}>Current Location</Text>
              <View style={styles.searchBar}>
                <MaterialIcons name="search" size={22} color={colors.orange} style={styles.searchIcon} />
                <TextInput
                  style={styles.searchInput}
                  value={locationSearch}
                  placeholder="Search Locations"
                  placeholderTextColor="#939598"
                  editable={!readOnly}
                  onChangeText={(val) => this.setState({ locationSearch: val })}
                />
                <MaterialIcons name="filter-list" size={22} color={colors.orange} />
              </View>

              {fromChipLabel ? (
                <View style={styles.toChipWrap}>
                  <View style={[styles.toChip, styles.fromChipFilled]}>
                    <Text style={styles.fromChipTxt} numberOfLines={3}>
                      {fromChipLabel}
                    </Text>
                  </View>
                </View>
              ) : null}

              {this.renderLocationTree()}
              {this.renderSelectedSection()}

              {!hideAvailableStock ? (
                <View>
              <View style={styles.stockHeader}>
                <Text style={styles.stockHeaderTxt} numberOfLines={2}>
                  Available Stock on Hand{fromLabel ? ` > (${fromLabel})` : ""}
                </Text>
                <MaterialIcons name="keyboard-arrow-down" size={22} color={colors.primary} />
              </View>

              <View style={styles.searchBar}>
                <MaterialIcons name="search" size={20} color={colors.orange} style={styles.searchIcon} />
                <TextInput
                  style={styles.searchInput}
                  value={itemSearch}
                  placeholder="Search items"
                  placeholderTextColor="#939598"
                  onChangeText={(val) => this.setState({ itemSearch: val })}
                />
                <MaterialIcons name="filter-list" size={20} color={colors.orange} />
              </View>

              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabsScroll}>
                {TYPE_TABS.map((tab) => {
                  const active = selectedStockType === tab.key;
                  return (
                    <TouchableOpacity
                      key={tab.key}
                      style={[styles.typeTab, active && styles.typeTabActive]}
                      onPress={() => this.onChangeStockType(tab.key)}
                    >
                      <Text style={[styles.typeTabTxt, active && styles.typeTabTxtActive]}>
                        {tab.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>

              <View style={styles.tableHeader}>
                <Text style={[styles.th, styles.colAdd]} />
                <Text style={[styles.th, styles.colName]}>Name</Text>
                <Text style={[styles.th, styles.colAmount]}>Amount</Text>
                <Text style={[styles.th, styles.colLocation]}>Location</Text>
              </View>

              {loading ? (
                <View style={styles.loadingBox}>
                  <ActivityIndicator size="large" color={colors.orange} />
                </View>
              ) : stock.length === 0 ? (
                <Text style={styles.emptyTxt}>
                  {fromLabel
                    ? "No items found at the current location"
                    : "Select a Current Location above to see available stock"}
                </Text>
              ) : (
                stock.map((row, idx) => (
                  <TouchableOpacity
                    key={`${row._id || row.item}-${idx}`}
                    style={styles.tableRow}
                    onPress={() => this.onAddStockItem(row)}
                  >
                    <View style={[styles.td, styles.colAdd]}>
                      <MaterialCommunityIcons name="plus-circle" size={26} color={colors.add} />
                    </View>
                    <Text style={[styles.td, styles.colName]} numberOfLines={2}>
                      {row.item}
                    </Text>
                    <Text style={[styles.td, styles.colAmount]} numberOfLines={1}>
                      {this.formatAmount(row.totalAmount, row.units)}
                    </Text>
                    <Text style={[styles.td, styles.colLocation]} numberOfLines={2}>
                      {row.locationLabel}
                    </Text>
                  </TouchableOpacity>
                ))
              )}
                </View>
              ) : null}
            </ScrollView>

            {this.state.infoMessage ? (
              <View style={styles.infoBanner}>
                <MaterialIcons name="info-outline" size={18} color={colors.primary} style={styles.infoIcon} />
                <Text style={styles.infoTxt}>{this.state.infoMessage}</Text>
              </View>
            ) : null}

            {readOnly ? (
              <View style={styles.footer}>
                <TouchableOpacity style={styles.saveBtn} onPress={onClose}>
                  <Text style={styles.saveTxt}>Close</Text>
                </TouchableOpacity>
              </View>
            ) : hasUserChanges ? (
              <View style={styles.footer}>
                <TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
                  <Text style={styles.cancelTxt}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={() => this.onSave()}>
                  <Text style={styles.saveTxt}>Save</Text>
                </TouchableOpacity>
              </View>
            ) : null}
          </View>
        </View>
      </Modal>
    );
  }
}

export default connect(
  null,
  (dispatch) => ({
    getItemsByLocation: (data, callback) => dispatch(getItemsByLocation(data, callback)),
    getIngredientLotsByName: (data, callback) => dispatch(getIngredientLotsByName(data, callback)),
  })
)(InventoryTransferFromLocationModal);

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.gray,
    paddingTop: Platform.OS === "ios" ? 40 : 10,
  },
  card: {
    flex: 1,
    backgroundColor: "#fff",
    margin: 10,
    borderRadius: 20,
    padding: 14,
    marginInline: isMobileWeb() ? "1" : "10%",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  backBtn: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: "#222",
    fontFamily: "Roboto",
  },
  bodyScroll: {
    flex: 1,
  },
  searchBar: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: colors.orange,
    borderRadius: 24,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 10,
  },
  searchIcon: { marginRight: 6 },
  searchInput: {
    flex: 1,
    fontSize: 15,
    color: "#333",
    padding: 0,
    fontFamily: "Roboto",
  },
  tabsScroll: {
    maxHeight: 44,
    marginBottom: 10,
  },
  typeTab: {
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: 18,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
  },
  typeTabActive: {
    backgroundColor: colors.primary,
  },
  typeTabTxt: {
    color: colors.primary,
    fontSize: 13,
    fontWeight: "600",
    fontFamily: "Roboto",
  },
  typeTabTxtActive: {
    color: "#fff",
  },
  sectionLabel: {
    fontSize: 15,
    fontWeight: "700",
    color: "#222",
    marginBottom: 8,
    fontFamily: "Roboto",
  },
  toChipWrap: {
    alignItems: "flex-start",
    marginBottom: 8,
  },
  toChip: {
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 8,
    maxWidth: "100%",
  },
  fromChipFilled: {
    backgroundColor: colors.primary,
    maxWidth: "100%",
  },
  fromChipTxt: {
    color: "#fff",
    fontSize: 13,
    fontFamily: "Roboto",
    flexShrink: 1,
  },
  toChipTxt: {
    color: colors.primary,
    fontSize: 14,
    fontFamily: "Roboto",
  },
  locationsScroll: {
    maxHeight: 220,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#e8e8e8",
    borderRadius: 8,
  },
  rootRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  rootRowAlt: { backgroundColor: "#f5f5f5" },
  rootRowBase: { backgroundColor: "#fff" },
  rootRowSelected: { backgroundColor: "#e8f0fe" },
  rootRowLabelBtn: {
    flex: 1,
    paddingRight: 8,
    minWidth: 0,
  },
  rootRowTxt: {
    fontSize: 15,
    color: "#222",
    fontFamily: "Roboto",
  },
  rootRowTxtSelected: {
    color: colors.primary,
    fontWeight: "700",
    textDecorationLine: "underline",
  },
  expandBtn: {
    paddingHorizontal: 6,
    paddingVertical: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  branchScroll: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    minHeight: 120,
  },
  branchRowWrap: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  branchCol: {
    minWidth: 180,
    width: 200,
    marginRight: 12,
    borderLeftWidth: 1,
    borderLeftColor: "#ddd",
    paddingLeft: 10,
  },
  branchRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  branchRowSelected: {
    backgroundColor: "#e8f0fe",
    borderRadius: 6,
  },
  branchLabelBtn: {
    flex: 1,
    minWidth: 0,
    paddingRight: 4,
  },
  branchTxt: {
    fontSize: 14,
    color: "#444",
    fontFamily: "Roboto",
  },
  branchTxtSelected: {
    color: colors.primary,
    fontWeight: "700",
    textDecorationLine: "underline",
  },
  selectedSection: { marginBottom: 12 },
  selectedGroup: { marginBottom: 8 },
  selectedGroupHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 6,
  },
  selectedGroupTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: "#222",
    fontFamily: "Roboto",
  },
  lotTableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  lotTableRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  lotTableRowSelected: {
    backgroundColor: colors.primary_light,
    borderRadius: 6,
  },
  lotTh: {
    fontSize: 12,
    fontWeight: "700",
    color: "#333",
    fontFamily: "Roboto",
  },
  lotTd: {
    fontSize: 13,
    color: "#333",
    fontFamily: "Roboto",
  },
  lotColRemove: { width: "10%", alignItems: "center" },
  lotColLot: { width: "28%" },
  lotColAmt: { width: "28%", flexDirection: "row", alignItems: "center" },
  lotColLoc: { width: "34%" },
  amountInput: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 4,
    minWidth: 48,
    fontSize: 13,
    color: "#222",
  },
  unitsTxt: { marginLeft: 4, fontSize: 12, color: "#666" },
  detailsBox: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
  },
  detailsTitle: {
    fontSize: 15,
    fontWeight: "700",
    color: "#222",
    marginBottom: 10,
    fontFamily: "Roboto",
  },
  detailsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  detailsCol: { flex: 1, alignItems: "center" },
  detailsLabel: {
    fontSize: 12,
    color: "#666",
    marginTop: 2,
    marginBottom: 6,
    fontFamily: "Roboto",
  },
  detailsChip: {
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: 18,
    paddingHorizontal: 10,
    paddingVertical: 6,
    maxWidth: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  detailsChipFilled: {
    backgroundColor: colors.primary,
  },
  detailsChipTxt: {
    color: colors.primary,
    fontSize: 12,
    textAlign: "center",
    fontFamily: "Roboto",
    flexShrink: 1,
  },
  detailsChipTxtFilled: {
    color: "#fff",
    fontSize: 12,
    textAlign: "center",
    fontFamily: "Roboto",
  },
  detailsHint: {
    marginTop: 4,
    fontSize: 10,
    color: "#999",
    fontFamily: "Roboto",
  },
  detailsArrow: { marginHorizontal: 6 },
  toDropdown: {
    marginTop: 8,
    width: "100%",
    maxHeight: 180,
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    backgroundColor: "#fff",
    overflow: "hidden",
    zIndex: 20,
  },
  toDropdownSearch: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  toDropdownInput: {
    flex: 1,
    marginLeft: 6,
    fontSize: 13,
    color: "#333",
    padding: 0,
    fontFamily: "Roboto",
  },
  toDropdownList: {
    maxHeight: 140,
  },
  toDropdownRow: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  toDropdownRowTxt: {
    fontSize: 13,
    color: "#333",
    fontFamily: "Roboto",
  },
  stockHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  stockHeaderTxt: {
    flex: 1,
    fontSize: 15,
    fontWeight: "700",
    color: "#222",
    fontFamily: "Roboto",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  th: { fontSize: 13, fontWeight: "700", color: "#333", fontFamily: "Roboto" },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingVertical: 10,
    paddingHorizontal: 4,
  },
  td: { fontSize: 13, color: "#333", fontFamily: "Roboto" },
  colAdd: { width: "10%", alignItems: "center" },
  colName: { width: "35%" },
  colAmount: { width: "25%" },
  colLocation: { width: "30%" },
  loadingBox: { padding: 24, alignItems: "center" },
  emptyTxt: {
    textAlign: "center",
    color: "#888",
    padding: 16,
    fontFamily: "Roboto",
  },
  infoBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: colors.primary_light,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginTop: 8,
  },
  infoIcon: { marginRight: 8, marginTop: 1 },
  infoTxt: {
    flex: 1,
    fontSize: 13,
    color: colors.primary,
    fontFamily: "Roboto",
    lineHeight: 18,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 12,
  },
  cancelBtn: {
    flex: 1,
    borderWidth: 1.5,
    borderColor: colors.quit,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: "center",
    marginRight: 6,
  },
  cancelTxt: {
    color: colors.quit,
    fontSize: 16,
    fontWeight: "700",
    fontFamily: "Roboto",
  },
  saveBtn: {
    flex: 1,
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: "center",
    marginLeft: 6,
  },
  saveTxt: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
    fontFamily: "Roboto",
  },
});
